import {decode}                             from 'jsonwebtoken';
import {SmtCache_v4_POST_userEvents}        from 'Smt/subjects/ContestOnline';
import {CUSTOM_EVENT_INTERNET_AVAILABILITY} from 'Smt/helpers/connection';

const STORAGE_KEY_PREFIX = 'smtUserEventsQueue:';
const SEND_INTERVAL_MS   = 120_000;
const MAX_QUEUE_LENGTH   = 200;
const STALE_QUEUE_TTL_MS = 60 * 60 * 1000;

type PairKey = 'tab' | 'window';

type StoredQueue = {
    updatedAtMs: number;
    events:      SmtCacheV4.BrowserEvent[];
};

type UserEventsTracker = {
    start: () => void;
    stop: () => void;
};

const readQueue = (storageKey: string): StoredQueue => {
    try {
        const data = localStorage.getItem(storageKey);
        return data ? JSON.parse(data) : {updatedAtMs: Date.now(), events: []};
    } catch (e) {
        return {updatedAtMs: Date.now(), events: []};
    }
};

const getOtherQueueKeys = (currentStorageKey: string): string[] =>
    Array.from({length: localStorage.length}, (_, i) => localStorage.key(i))
        .filter((key): key is string => !!key && key !== currentStorageKey && key.startsWith(STORAGE_KEY_PREFIX))
;

const writeQueue = (storageKey: string, events: SmtCacheV4.BrowserEvent[]) => {
    if (events.length === 0) {
        localStorage.removeItem(storageKey);
    } else {
        const queue: StoredQueue = {updatedAtMs: Date.now(), events};
        localStorage.setItem(storageKey, JSON.stringify(queue));
    }
};

const isStale = (updatedAtMs: number) => Date.now() - updatedAtMs > STALE_QUEUE_TTL_MS;

const cleanupStaleQueues = (currentStorageKey: string) => {
    getOtherQueueKeys(currentStorageKey)
        .filter((key) => isStale(readQueue(key).updatedAtMs))
        .forEach((key) => localStorage.removeItem(key))
    ;
};

const getSessionId = (bearer: string) =>
    (decode(bearer, {json: true}) as SmtJWT.JWTToken)?.sub ?? bearer
;

const createUserEventsTracker = (bearer: string): UserEventsTracker => {
    const storageKey = `${STORAGE_KEY_PREFIX}${getSessionId(bearer)}`;
    const pairStart: Partial<Record<PairKey, number>> = {};
    let sendTimer: number = null;

    const takeDuration = (key: PairKey) => {
        const start = pairStart[key];
        delete pairStart[key];
        return start !== void(0) ? Date.now() - start : void(0);
    };

    const pushEvent = (type: SmtCacheV4.BrowserEventType, durationMS?: number) => {
        const event: SmtCacheV4.BrowserEvent = {
            type,
            timestampMS: Date.now(),
            ...(durationMS !== void(0) ? {durationMS} : {}),
        };

        const {events} = readQueue(storageKey);
        events.push(event);

        if (events.length > MAX_QUEUE_LENGTH) {
            events.splice(0, events.length - MAX_QUEUE_LENGTH);
        }

        writeQueue(storageKey, events);
    };

    const sendQueuedEvents = () => {
        const {events} = readQueue(storageKey);

        if (events.length === 0) {
            return;
        }

        const request: SmtCacheV4.UserEventsStoreRequest = {
            sentAsTimestampMs: Date.now(),
            events,
        };

        SmtCache_v4_POST_userEvents(request, bearer)
            .then(() => writeQueue(storageKey, []))
        ;
    };

    const onVisibilityChange = () => {
        if (document.hidden) {
            pairStart.tab = Date.now();
            pushEvent('tab_hidden');
        } else {
            pushEvent('tab_visible', takeDuration('tab'));
        }
    };

    const onWindowBlur = () => {
        pairStart.window = Date.now();
        pushEvent('window_blur');
    };

    const onWindowFocus = () => {
        pushEvent('window_focus', takeDuration('window'));
    };

    const onPaste = () => {
        pushEvent('paste');
    };

    const onCopy = () => {
        pushEvent('copy');
    };

    const onBeforeUnload = () => {
        pushEvent('beforeunload_attempt');
        sendQueuedEvents();
    };

    const onInternetAvailability = () => {
        pushEvent('internet_on');
    };

    const start = () => {
        cleanupStaleQueues(storageKey);

        window.addEventListener('blur', onWindowBlur);
        window.addEventListener('focus', onWindowFocus);
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('paste', onPaste);
        document.addEventListener('copy', onCopy);
        document.addEventListener('visibilitychange', onVisibilityChange);
        document.addEventListener(CUSTOM_EVENT_INTERNET_AVAILABILITY, onInternetAvailability);

        sendTimer = window.setInterval(sendQueuedEvents, SEND_INTERVAL_MS);
    };

    const stop = () => {
        window.removeEventListener('blur', onWindowBlur);
        window.removeEventListener('focus', onWindowFocus);
        window.removeEventListener('beforeunload', onBeforeUnload);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        document.removeEventListener('paste', onPaste);
        document.removeEventListener('copy', onCopy);
        document.removeEventListener(CUSTOM_EVENT_INTERNET_AVAILABILITY, onInternetAvailability);

        if (sendTimer !== null) {
            window.clearInterval(sendTimer);
            sendTimer = null;
        }

        sendQueuedEvents();
    };

    return {start, stop};
};

export {createUserEventsTracker};
